import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import useAuthToken from '../functions/useAuthToken';
import { useDiaryMaintenanceApi } from '../functions/useDiaryMaintenanceApi';
import type {
  AdminClientOption,
  DiaryHealthSummary,
  ReminderRepairResult,
} from '../models/diaryMaintenance';

const HORIZON_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '12 months' },
  { value: 730, label: '24 months' },
];

const dateFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const formatUtc = (iso: string) => dateFmt.format(new Date(iso));

function describeOffset(minutes: number): string {
  if (minutes === 0) return 'At start';
  const abs = Math.abs(minutes);
  const when = minutes < 0 ? 'before' : 'after';
  if (abs % 1440 === 0) return `${abs / 1440} day${abs === 1440 ? '' : 's'} ${when}`;
  if (abs % 60 === 0) return `${abs / 60} hour${abs === 60 ? '' : 's'} ${when}`;
  return `${abs} min ${when}`;
}

export default function DiaryMaintenancePage() {
    const getAccessToken = useAuthToken();
  const api = useDiaryMaintenanceApi();

  const [clients, setClients] = useState<AdminClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [client, setClient] = useState<AdminClientOption | null>(null);
  const [horizonDays, setHorizonDays] = useState(365);

  const [summary, setSummary] = useState<DiaryHealthSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [preview, setPreview] = useState<ReminderRepairResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyResult, setApplyResult] = useState<ReminderRepairResult | null>(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Load the client list once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
          const token = await getAccessToken();
          if (!token) { setError('Invalid access token'); }
          else {

              const result = await api.getClients(token);
              if (!cancelled) setClients(result);
          }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, getAccessToken]);

  const loadSummary = useCallback(async () => {
    if (!client) return;
    setSummaryLoading(true);
    setError(null);
    try {
        const token = await getAccessToken();
        if (!token) { setError('Invalid access token'); }
        else { setSummary(await api.getSummary(token, client.clientId, horizonDays)); }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSummaryLoading(false);
    }
  }, [api, client, horizonDays, getAccessToken]);

  // A change of client or horizon makes any preview/result stale.
  useEffect(() => {
    setPreview(null);
    setApplyResult(null);
    setSummary(null);
    if (client) void loadSummary();
  }, [client, horizonDays, loadSummary]);

  const runPreview = async () => {
    if (!client) return;
    setPreviewLoading(true);
    setApplyResult(null);
    setError(null);
    try {
        const token = await getAccessToken();
        if (!token) { setError('Invalid access token'); }
        else {
            setPreview(await api.previewReminderRepair(token, client.clientId, horizonDays));
        }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!client) return;
    setConfirmOpen(false);
    setApplying(true);
    setError(null);
    try {
        const token = await getAccessToken();
        if (!token) { setError('Invalid access token'); }
        else {
            const result = await api.applyReminderRepair(token, client.clientId, horizonDays);
            setApplyResult(result);
            setPreview(null);
            await loadSummary();
        }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

    const previewCount = preview?.details.length ?? 0;
    const missedCount = preview?.missed.length ?? 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Diary maintenance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check the health of a client's diary reminders and repair missing ones.
        </Typography>
      </Stack>

      {/* Client + horizon */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 260 }}
            options={clients}
            loading={clientsLoading}
            value={client}
            onChange={(_, value) => setClient(value)}
            getOptionLabel={(o) => `${o.name} (#${o.clientId})`}
            isOptionEqualToValue={(a, b) => a.clientId === b.clientId}
            renderInput={(params) => <TextField {...params} label="Client" size="small" />}
          />
          <TextField
            select
            size="small"
            label="Look ahead"
            value={horizonDays}
            onChange={(e) => setHorizonDays(Number(e.target.value))}
            sx={{ width: { xs: '100%', sm: 160 } }}
          >
            {HORIZON_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => void loadSummary()}
            disabled={!client || summaryLoading}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!client && (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          Choose a client to see their diary health.
        </Typography>
      )}

      {client && (
        <Stack spacing={3}>
          <HealthSummarySection summary={summary} loading={summaryLoading} />

          {/* Repair */}
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Repair missing reminders
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Creates pending occurrences for enabled reminders on planned items that are
                    missing one. Existing occurrences are never changed.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexShrink={0}>
                  <Button
                    variant="outlined"
                    startIcon={
                      previewLoading ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon />
                    }
                    onClick={() => void runPreview()}
                    disabled={previewLoading || applying}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <BuildOutlinedIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={!preview || previewCount === 0 || applying}
                  >
                    Apply
                  </Button>
                </Stack>
              </Stack>

              {applyResult && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Created {applyResult.occurrencesCreated} reminder
                  {applyResult.occurrencesCreated === 1 ? '' : 's'} across{' '}
                  {new Set(applyResult.details.map((d) => d.diaryItemId)).size} item(s).
                  {applyResult.occurrencesCreated !== previewCount && previewCount > 0 && (
                    <> This differs from the preview because reminders changed in the meantime.</>
                  )}
                </Alert>
              )}

              {!preview && !applyResult && (
                <Typography variant="body2" color="text.secondary">
                  Run a preview to see what would be created.
                </Typography>
              )}

              {preview && previewCount === 0 && (
                <Alert severity="info">
                  Nothing to repair. All {preview.itemsScanned} planned items have their reminders.
                </Alert>
              )}

              {preview && previewCount > 0 && <RepairPreviewTable result={preview} />}
            </CardContent>
          </Card>
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Create {previewCount} reminders?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create {previewCount} pending reminder occurrence
            {previewCount === 1 ? '' : 's'} for <strong>{client?.name}</strong>. They will be sent
            by the reminder worker at their scheduled times.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void runApply()}>
            Create reminders
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function HealthSummarySection({
  summary,
  loading,
}: {
  summary: DiaryHealthSummary | null;
  loading: boolean;
}) {
  if (loading && !summary) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!summary) return null;

  const tiles: { label: string; value: number; tone?: 'warning' | 'error'; hint?: string }[] = [
    { label: 'Planned items', value: summary.plannedItems },
    { label: 'Enabled reminders', value: summary.enabledReminders },
    {
      label: 'Missing reminders',
      value: summary.missingOccurrences,
      tone: summary.missingOccurrences > 0 ? 'warning' : undefined,
      hint: 'Future occurrences the repair would create',
    },
    {
      label: 'Overdue pending',
      value: summary.overduePending,
      tone: summary.overduePending > 0 ? 'error' : undefined,
      hint: 'Pending more than 15 min past fire time',
    },
    {
      label: 'Items with no date',
      value: summary.plannedItemsWithoutDate,
      hint: 'Events without a start or tasks without a due date',
      },
      {
          label: 'Past Missing reminders',
          value: summary.missedPastReminders,
          hint: 'Events where reminders where not generated and dates now passed',
      },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)',
          },
          opacity: loading ? 0.6 : 1,
          transition: 'opacity 150ms',
        }}
      >
        {tiles.map((t) => (
          <Card
            key={t.label}
            variant="outlined"
            sx={{ borderColor: t.tone ? `${t.tone}.main` : undefined }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                {t.label}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={600}
                color={t.tone ? `${t.tone}.main` : 'text.primary'}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {t.value.toLocaleString()}
              </Typography>
              {t.hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                  {t.hint}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {Object.keys(summary.occurrencesByStatus).length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
            All occurrences:
          </Typography>
          {Object.entries(summary.occurrencesByStatus).map(([status, count]) => (
            <Chip key={status} size="small" variant="outlined" label={`${status}: ${count.toLocaleString()}`} />
          ))}
        </Stack>
      )}

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
}

function RepairPreviewTable({ result }: { result: ReminderRepairResult }) {
  const itemCount = new Set(result.details.map((d) => d.diaryItemId)).size;

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {result.details.length} missing reminder{result.details.length === 1 ? '' : 's'} across{' '}
        {itemCount} item{itemCount === 1 ? '' : 's'} (of {result.itemsScanned} scanned). Preview
        taken {formatUtc(result.generatedUtc)}.
      </Alert>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Scheduled for</TableCell>
              <TableCell>Reminder</TableCell>
              <TableCell>Will fire</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.details.map((d) => (
              <TableRow key={`${d.reminderId}-${d.fireAtUtc}`} hover>
                <TableCell>
                  {d.title || <em>Untitled</em>}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    #{d.diaryItemId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={d.itemType} variant="outlined" />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUtc(d.anchorUtc)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{describeOffset(d.offsetMinutes)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUtc(d.fireAtUtc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
